import type { DealFormStep } from './mixpanelEvents';
import type {
  ICompanyDraft,
  IContactDraft,
  ICreateLocationDealRequest,
  IHubSpotCompany,
  IHubSpotContact,
  INearbyPlace,
  LandownerType,
} from '@/types/api';
import { landownerTypeFromPlace, linkedPlaceType } from './landownerType';

/** Whether a record is attached from the CRM or created alongside the deal. Never both. */
export type RecordMode = 'attach' | 'create';

export interface DealCreationDraft {
  companyMode: RecordMode;
  companyId: string;
  company: ICompanyDraft;
  /**
   * The attached record itself, kept beside its id so the step can show what was picked after it
   * remounts. Deriving the display from the live CRM search instead left the draft holding an id
   * with nothing on screen — the BD saw an empty picker and an enabled Continue.
   */
  attachedCompany?: IHubSpotCompany;

  contactMode: RecordMode;
  contactId: string;
  contact: IContactDraft;
  attachedContact?: IHubSpotContact;

  /**
   * The point of interest the deal is about: the location's own place when it has one, otherwise
   * picked from the places around it or typed. The deal's name is composed from it either way.
   */
  poiName: string;
  /**
   * The place `poiName` was picked from, when it was. It travels with the request so the location
   * gets linked to it. Typing over the name drops it: a name the BD wrote is not a place.
   */
  place?: INearbyPlace;
  /**
   * The city the deal's name carries, pre-filled from the location and editable so an incomplete
   * address does not leave the name headless. It names the deal only — the company's own city
   * belongs to the company step.
   */
  city: string;
  ownerEmail: string;
  zoneRef: string;
  /** The site's business category. Empty leaves it for the BD to complete. */
  landownerType: LandownerType | '';
}

export function emptyDraft(poiName = '', city = ''): DealCreationDraft {
  return {
    companyMode: 'attach',
    companyId: '',
    company: { name: '' },
    contactMode: 'attach',
    contactId: '',
    contact: { email: '' },
    poiName,
    city,
    ownerEmail: '',
    zoneRef: '',
    landownerType: '',
  };
}

/** Whether the company step has enough to move on: one of the two branches, never both. */
export function isCompanyStepComplete(draft: DealCreationDraft): boolean {
  return draft.companyMode === 'attach' ? draft.companyId !== '' : draft.company.name.trim() !== '';
}

/** Whether the contact step has enough to move on. A deal has to have someone to talk to. */
export function isContactStepComplete(draft: DealCreationDraft): boolean {
  return draft.contactMode === 'attach'
    ? draft.contactId !== ''
    : draft.contact.email.trim() !== '';
}

/**
 * The deal's name, always `<city> - <POI>` so every deal reads the same way in the CRM. The city
 * drops out rather than leaving a dangling dash when the location has no address to pre-fill it.
 */
export function composeDealName(draft: DealCreationDraft): string {
  return [draft.city.trim(), draft.poiName.trim()].filter((part) => part !== '').join(' - ');
}

/**
 * The places offered for the typed fragment. The whole list comes back when the field is empty or
 * holds the picked place: narrowing to it would answer the one case the list exists for with a
 * list of one.
 */
export function matchingPlaces(
  places: readonly INearbyPlace[],
  typed: string,
  picked?: INearbyPlace,
): INearbyPlace[] {
  const needle = typed.trim().toLowerCase();
  if (needle === '' || (picked && needle === picked.name.toLowerCase())) return [...places];
  return places.filter(
    (place) =>
      place.name.toLowerCase().includes(needle) || place.type.toLowerCase().includes(needle),
  );
}

/** Picking a place recomputes the category. One the icon does not decide clears it. */
export function placePicked(place: INearbyPlace): Partial<DealCreationDraft> {
  return { poiName: place.name, place, landownerType: landownerTypeFromPlace(place) };
}

/**
 * Typing over the name drops the place. A category that place had filled in drops with it; one
 * the BD chose stays.
 */
export function placeNameEdited(
  draft: DealCreationDraft,
  poiName: string,
): Partial<DealCreationDraft> {
  const patch: Partial<DealCreationDraft> = { poiName, place: undefined };
  const prefilled = draft.place ? landownerTypeFromPlace(draft.place) : '';
  if (prefilled !== '' && draft.landownerType === prefilled) patch.landownerType = '';
  return patch;
}

/** The deal needs a POI; the city, the owner, the zone and the site type are optional. */
export function isDealStepComplete(draft: DealCreationDraft): boolean {
  return draft.poiName.trim() !== '';
}

/**
 * How many of a step's optional fields carry a value — what the BD volunteered beyond what the
 * step requires. Zero for a step whose record is attached: an attached record's own fields are
 * the CRM's, not this form's. The point counts once, because its two halves are one answer.
 */
export function filledOptionalCount(draft: DealCreationDraft, step: DealFormStep): number {
  if (step === 'company') {
    if (draft.companyMode === 'attach') return 0;
    const { street, postcode, city, country, address, latitude, longitude } = draft.company;
    const filled = [street, postcode, city, country, address].filter(isFilled).length;
    return filled + (latitude !== undefined && longitude !== undefined ? 1 : 0);
  }
  if (step === 'contact') {
    if (draft.contactMode === 'attach') return 0;
    const { first_name, last_name, phone, job_title } = draft.contact;
    return [first_name, last_name, phone, job_title].filter(isFilled).length;
  }
  return [draft.ownerEmail, draft.zoneRef, draft.landownerType].filter(isFilled).length;
}

function isFilled(value: string | undefined): boolean {
  return value !== undefined && value.trim() !== '';
}

/**
 * Map the draft onto the request. Only the chosen branch of each pair travels, because the
 * contract rejects a record that is both attached and created. Empty optional fields are dropped
 * rather than sent blank: the backend reads absent as "leave it unset" and an empty string as a
 * value the BD typed.
 */
export function toCreateRequest(draft: DealCreationDraft): ICreateLocationDealRequest {
  const request: ICreateLocationDealRequest = { deal_name: composeDealName(draft) };

  if (draft.companyMode === 'attach') request.company_id = draft.companyId;
  else request.company = compactCompany(draft.company);

  if (draft.contactMode === 'attach') request.contact_id = draft.contactId;
  else request.contact = compactContact(draft.contact);

  const ownerEmail = draft.ownerEmail.trim();
  if (ownerEmail !== '') request.owner_email = ownerEmail;

  const zoneRef = draft.zoneRef.trim();
  if (zoneRef !== '') request.zone_ref = zoneRef;

  if (draft.landownerType !== '') request.landowner_type = draft.landownerType;

  if (draft.place) {
    const { name, latitude, longitude } = draft.place;
    request.point_of_interest = {
      name,
      type: linkedPlaceType(draft.place),
      latitude,
      longitude,
    };
  }

  return request;
}

function compactCompany(company: ICompanyDraft): ICompanyDraft {
  // The address lines travel even when empty: they are pre-filled from the location and a line the
  // BD cleared is an answer, not an absence.
  const out: ICompanyDraft = {
    name: company.name.trim(),
    street: company.street?.trim() ?? '',
    postcode: company.postcode?.trim() ?? '',
    city: company.city?.trim() ?? '',
    country: company.country ?? '',
  };
  if (company.address?.trim()) out.address = company.address.trim();
  // The two travel together or not at all: a company the BD did not place must not be handed the
  // point 0,0.
  if (company.latitude !== undefined && company.longitude !== undefined) {
    out.latitude = company.latitude;
    out.longitude = company.longitude;
  }
  return out;
}

function compactContact(contact: IContactDraft): IContactDraft {
  const out: IContactDraft = { email: contact.email.trim() };
  if (contact.first_name?.trim()) out.first_name = contact.first_name.trim();
  if (contact.last_name?.trim()) out.last_name = contact.last_name.trim();
  if (contact.phone?.trim()) out.phone = contact.phone.trim();
  if (contact.job_title?.trim()) out.job_title = contact.job_title.trim();
  return out;
}
